import { Button, Card, Form, Input, Select, Space, Typography } from 'antd';
import {
  LockOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { setTenantRuntimeSettings } from '../lib/tenant';
import { DeploymentAccessBadge } from '../components/DeploymentAccessBadge';
import { AuthBrand, usePublicBranding } from '../components/AuthBrand';

interface LoginForm {
  username: string;
  password: string;
}

export function LoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  const [loading, setLoading] = useState(false);
  const { siteName, logoUrl } = usePublicBranding();

  useEffect(() => {
    let active = true;
    void fetch('/api/v1/system/setup/status')
      .then(async (response) => {
        if (!response.ok) return;
        const data = (await response.json()) as { initialized: boolean };
        if (active && !data.initialized) {
          void navigate('/setup', { replace: true });
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [navigate]);

  async function submit(values: LoginForm) {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(values),
      });
      if (!response.ok)
        throw new Error(
          response.status === 401
            ? t('login.invalid')
            : response.status === 423
              ? t('login.locked')
              : response.status === 429
                ? t('login.rateLimited')
                : t('login.unavailable'),
        );
      const user = (await response.json()) as {
        mustChangePassword: boolean;
        locale: 'zh-CN' | 'en-US';
        tenant?: {
          systemName?: string | null;
          systemNameZh?: string | null;
          systemNameEn?: string | null;
          timezone?: string | null;
        };
      };
      setTenantRuntimeSettings(user.tenant);
      await i18n.changeLanguage(user.locale);
      window.location.assign(
        user.mustChangePassword ? '/account?tab=settings' : '/',
      );
    } catch (error) {
      await messageApi.error(
        error instanceof Error ? error.message : t('login.invalid'),
      );
    } finally {
      setLoading(false);
    }
  }

  return (
    <main className="login-page">
      {contextHolder}
      <header className="login-header">
        <AuthBrand siteName={siteName} logoUrl={logoUrl} />
        <Select
          aria-label={t('common.language')}
          value={i18n.resolvedLanguage ?? 'zh-CN'}
          onChange={(value) => void i18n.changeLanguage(value)}
          options={[
            { value: 'zh-CN', label: '简体中文' },
            { value: 'en-US', label: 'English' },
          ]}
        />
      </header>
      <section className="login-stage" aria-labelledby="login-title">
        <div className="login-intro">
          <span className="eyebrow">CELL BANK</span>
          <Typography.Title id="login-title" level={1}>
            {t('login.title', { siteName })}
          </Typography.Title>
          <Typography.Paragraph>{t('login.subtitle')}</Typography.Paragraph>
        </div>
        <Card className="login-card" variant="borderless">
          <Form<LoginForm>
            layout="vertical"
            size="large"
            autoComplete="on"
            onFinish={(values) => void submit(values)}
          >
            <Form.Item
              label={t('login.username')}
              name="username"
              rules={[{ required: true }]}
            >
              <Input prefix={<UserOutlined />} autoComplete="username" />
            </Form.Item>
            <Form.Item
              label={t('login.password')}
              name="password"
              rules={[{ required: true }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                autoComplete="current-password"
              />
            </Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {t('login.submit')}
            </Button>
            <div className="login-links">
              <Link to="/forgot-password">{t('login.forgotPassword')}</Link>
            </div>
          </Form>
        </Card>
      </section>
      <footer className="login-footer">
        <Space split={<span>·</span>}>
          <DeploymentAccessBadge />
          <span>{t('login.version')}</span>
        </Space>
      </footer>
    </main>
  );
}
